import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * Arms the landing page and reveals blocks as they scroll into view.
 *
 * The landing CSS treats the finished state as its default; nothing animates
 * until this controller sets `data-lp-armed` on the root. So if JS never runs,
 * or the visitor asked for reduced motion, the page simply renders complete.
 *
 * Arming waits one frame before observing, which lets the "before" state paint
 * once — that frame is what turns the first screenful into a load sequence
 * instead of a pop.
 */
export default class extends Controller {
  static targets = ["item", "progress"]

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.element.dataset.lpArmed = "true"
    this.trackProgress()

    this.frame = requestAnimationFrame(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return
            entry.target.classList.add("is-in")
            this.observer.unobserve(entry.target)
          })
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.08 }
      )

      this.itemTargets.forEach((el) => this.observer.observe(el))
    })
  }

  /**
   * Hairline under the header that fills as the page is read — the same
   * progress a reader gets on a story, applied to the landing page itself.
   */
  trackProgress() {
    if (!this.hasProgressTarget) return

    this.onScroll = () => {
      if (this.scrollFrame) return
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null
        const doc = document.documentElement
        const span = doc.scrollHeight - window.innerHeight
        const ratio = span > 0 ? Math.min(Math.max(window.scrollY / span, 0), 1) : 0
        this.progressTarget.style.setProperty("--lp-progress", ratio.toFixed(4))
      })
    }

    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
    this.onScroll()
  }

  disconnect() {
    if (this.frame) cancelAnimationFrame(this.frame)
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame)
    if (this.onScroll) {
      window.removeEventListener("scroll", this.onScroll)
      window.removeEventListener("resize", this.onScroll)
    }
    if (this.observer) this.observer.disconnect()
    delete this.element.dataset.lpArmed
  }
}
